import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../../components/layout/MainLayout';
import { Activity, Loader2, X, Shield } from 'lucide-react';
import { auditApi } from '../../services/api';
import { toast } from 'sonner';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const UserActivity = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const data = await auditApi.getUserActivity();
        if (active) setUsers(data.items);
      } catch (err) {
        toast.error(err.message || 'Failed to load user activity');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  const openDetail = async (userId) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      const data = await auditApi.getUserActivityDetail(userId);
      setDetail(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load user detail');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <MainLayout title="User Activity" subtitle="Per-user platform activity">
      <div data-testid="user-activity-page">
        <div className="flex items-center gap-2 text-gray-500 mb-6">
          <Activity className="w-5 h-5" />
          <span className="text-sm">{users.length} active users</span>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="user-activity-loading">
              <Loader2 className="w-7 h-7 animate-spin text-blue-500" />
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8B] text-white">
                  <th className="px-6 py-4 text-left">User</th>
                  <th className="px-6 py-4 text-left">Sessions</th>
                  <th className="px-6 py-4 text-left">Page Views</th>
                  <th className="px-6 py-4 text-left">Searches</th>
                  <th className="px-6 py-4 text-left">Events</th>
                  <th className="px-6 py-4 text-left">Last Seen</th>
                </tr>
              </thead>
              <tbody data-testid="user-activity-table-body">
                {users.length === 0 ? (
                  <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-500">No user activity recorded yet.</td></tr>
                ) : users.map((u, i) => (
                  <motion.tr
                    key={u.user_id}
                    data-testid={`user-activity-row-${u.user_id}`}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                    onClick={() => openDetail(u.user_id)}
                    className="border-b border-gray-500/10 hover:bg-blue-500/5 cursor-pointer"
                  >
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center gap-2">
                        {u.role === 'admin' && <Shield className="w-4 h-4 text-purple-500" />}
                        <span className="font-medium">{u.full_name}</span>
                      </div>
                      <span className="text-xs text-gray-400">{u.email}</span>
                    </td>
                    <td className="px-6 py-4 text-sm">{u.session_count}</td>
                    <td className="px-6 py-4 text-sm">{u.page_views}</td>
                    <td className="px-6 py-4 text-sm">{u.search_count}</td>
                    <td className="px-6 py-4 text-sm">{u.event_count}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{fmt(u.last_seen)}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          )}
        </motion.div>

        <AnimatePresence>
          {detail && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={() => setDetail(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                className="glass-card p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()} data-testid="user-activity-detail-modal"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">{detail.user?.full_name || 'User'} Activity</h3>
                  <button onClick={() => setDetail(null)} className="p-2 rounded-lg hover:bg-gray-500/10" data-testid="user-activity-detail-close"><X className="w-5 h-5" /></button>
                </div>
                {detailLoading || detail.loading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
                ) : (
                  <div className="space-y-5">
                    <p className="text-sm text-gray-500">{detail.user?.email}</p>
                    <div>
                      <h4 className="font-semibold text-sm mb-2">Sessions ({detail.sessions?.length || 0})</h4>
                      <div className="space-y-2">
                        {(detail.sessions || []).map(s => (
                          <div key={s.session_id} className="p-3 rounded-lg bg-gray-500/5 text-xs flex justify-between">
                            <span>{s.page_count}pv · {s.search_count}q · {s.duration_seconds}s</span>
                            <span className="text-gray-500">{fmt(s.start)}</span>
                          </div>
                        ))}
                        {(!detail.sessions || detail.sessions.length === 0) && <p className="text-xs text-gray-500">No sessions.</p>}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-2">Admin Actions ({detail.audit_logs?.length || 0})</h4>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {(detail.audit_logs || []).map(a => (
                          <div key={a.id} className="p-2 rounded bg-gray-500/5 text-xs flex justify-between">
                            <span className="capitalize text-blue-400">{(a.action || '').replace(/_/g, ' ')}</span>
                            <span className="text-gray-500">{fmt(a.timestamp)}</span>
                          </div>
                        ))}
                        {(!detail.audit_logs || detail.audit_logs.length === 0) && <p className="text-xs text-gray-500">No admin actions.</p>}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-2">Recent Events ({detail.events?.length || 0})</h4>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {(detail.events || []).map(ev => (
                          <div key={ev.id} className="p-2 rounded bg-gray-500/5 text-xs flex justify-between">
                            <span><span className="text-blue-400">{ev.event_type}</span> {ev.path || ''}</span>
                            <span className="text-gray-500 whitespace-nowrap ml-2">{fmt(ev.timestamp)}</span>
                          </div>
                        ))}
                        {(!detail.events || detail.events.length === 0) && <p className="text-xs text-gray-500">No events.</p>}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MainLayout>
  );
};

export default UserActivity;
